import { ReactElement, SVGAttributes } from 'react'

import defaultImage from '@/assets/placeholder.png'
import { Listing } from '@/data/getListing'
import { getId } from '../ModalGallery/embed'
import { CarouselGalleryAsset } from '../ModalGallery/GalleryContext'

export type ListingGalleryAsset = CarouselGalleryAsset & {
  icon?: ({ className, ...props }: SVGAttributes<SVGElement>) => ReactElement
  cb: () => void
}

export const remapListingAssetsToGallery = (listing: Listing) => {
  // If hero image is the same as first image, then push all references by 1
  const galleryIndex = listing.images.length > 1 && listing.image?.url === listing.images[0].url ? 1 : 0
  const images: CarouselGalleryAsset[] = listing.images.map((image, index) => {
    // Sometimes there is no dimensions, so assume it's 1200 x 1200.
    const [width, height] = image.dimensions ? image.dimensions.split('x').map((d) => parseInt(d, 10)) : [1200, 1200]
    return {
      kind: 'image',
      url: `https:${image.url}`,
      thumbnailUrl: `https:${image.thumbs._200x150}`,
      caption: null,
      id: index.toString(),
      width,
      height,
      alt: null,
    } satisfies CarouselGalleryAsset
  })

  const has3DTour = listing.links.filter((link) => link.link_type === 'virtual_tour')
  const threeDTourIndex = has3DTour.length > 0 ? images.length : null
  if (has3DTour.length > 0) {
    images.push(
      ...has3DTour.map(
        (link, index) =>
          ({
            kind: 'iframe',
            url: link.link_url,
            thumbnailUrl: get3DTourImage(link.link_url, images[galleryIndex + 1]?.url ?? defaultImage.src),
            type: 'iframe',
            caption: null,
            id: String(images.length + index),
            width: 1200,
            height: 800,
            alt: null,
          }) satisfies CarouselGalleryAsset,
      ),
    )
  }

  const hasVideo = listing.links.filter((link) => link.link_type === 'video_link')
  const videoIndex = hasVideo.length > 0 ? images.length : null
  if (hasVideo.length > 0) {
    images.push(
      ...hasVideo.map(
        (link, index) =>
          ({
            kind: 'iframe',
            url: link.link_url,
            thumbnailUrl: getVideoUrl(link.link_url, images[galleryIndex + 2]?.url ?? defaultImage.src, true),
            type: 'iframe',
            caption: null,
            id: String(images.length + index),
            width: 1200,
            height: 800,
            alt: null,
          }) satisfies CarouselGalleryAsset,
      ),
    )
  }

  const floorplanIndex = listing.floorplans.length > 0 ? images.length : null
  // Add in any floor plans
  if (listing.floorplans.length) {
    images.push(
      ...listing.floorplans.map(
        (floorplan, index) =>
          ({
            kind: 'image',
            url: `https:${floorplan.url}`,
            thumbnailUrl: `https:${floorplan.thumbs._400x400}`,
            caption: null,
            id: String(images.length + index),
            width: 1200,
            height: 1200,
            alt: null,
          }) satisfies CarouselGalleryAsset,
      ),
    )
  }
  return { images, galleryIndex, videoIndex, threeDTourIndex, floorplanIndex }
}

export const get3DTourImage = (imageUrl: string, backupUrl: string) => {
  try {
    if (!imageUrl.includes('matterport')) {
      throw new Error('Not matterport link!')
    }

    const myUrl = new URL(imageUrl)
    if (!myUrl.searchParams.has('m')) {
      throw new Error('No matterport id')
    }
    const matterportId = myUrl.searchParams.get('m')
    return `https://my.matterport.com/api/v2/player/models/${matterportId as string}/thumb/`
  } catch {
    return backupUrl
  }
}

export const getVideoUrl = (imageUrl: string, backupUrl: string, thumbnail = false) => {
  try {
    const videoId = getId(imageUrl)
    if (!videoId) {
      throw new Error('No video id')
    }
    if (thumbnail) {
      return `https://i3.ytimg.com/vi/${videoId}/hqdefault.jpg`
    }
    return `https://i3.ytimg.com/vi/${videoId}/maxresdefault.jpg`
  } catch {
    return backupUrl
  }
}
